/**
 * 当前附加费率 / 刷卡费率 —— 与后端逐字同义的纯函数（不碰 Vue / store / 时钟）。
 *
 * 后端是唯一权威：下单时后端自己按 `Venue::getCurrentSurcharge`（contactfree-backend `api/models/Venue.php`
 * 1483-1546）取费率、自己算钱，前端报上去的 amount 只用来比对，差 0.1 以上就 `##9##` 拒单。
 * 所以这里每一步都照 PHP 7.4 的语义写，包括它的怪癖（审计 00-backend-spec §2.2 / §7 Q1–Q3、Q6–Q8）：
 *
 *   1. surcharges 为 null / 缺 date 键：不报错，跳过 date。
 *   2. 遍历**全部** date 条目（不 break、不 find）：日期等于门店今天、`'H:i:s' >= fromHour && 'H:i:s' <= toHour`
 *      （PHP 字符串比较 → 起始那一分钟算、结束那一分钟不算；跨午夜永不命中；缺 toHour 永不命中），
 *      命中就覆盖，**最后命中的赢**。date 没有开关。
 *   3. date 命中后费率为 0 → 落回 weekly（后端判的是 `$surchargeRate == 0`）。
 *   4. weekly：按门店时区的星期取；`enable` 按 PHP 真值（字符串 "false" 也算开）；
 *      `rate` 可能是字符串或数字，`(float)rate / 100`，**不取整**。
 *
 * 期望值见 tests/unit/fixtures/surcharge/php-getCurrentSurcharge-cases.json（PHP 7.4 直接跑后端那段函数体）。
 */

import { phpTruthy, phpFloat } from '@/utils/phpSemantics'

/**
 * PHP 7.4 里 `$now <=> $bound`，$now 恒为 'H:i:s'（非数字串）。
 * - bound 是字符串：逐字节比较（JS 的字符串比较对 ASCII 等价）；
 * - bound 是 null / bool：两边转 bool，非空串恒为 true（所以 `>= null` 真、`<= null` 假）；
 * - bound 是数字：$now 按开头的数字转成数（"18:30:00" → 18）再比；
 * - bound 是数组：数组恒大。
 */
function phpCompareNow(now, bound) {
  if (typeof bound === 'string') return now < bound ? -1 : now > bound ? 1 : 0
  if (bound === null || bound === undefined || typeof bound === 'boolean') {
    const b = bound === true
    return b ? 0 : 1
  }
  if (typeof bound === 'number') {
    const a = phpFloat(now)
    return a < bound ? -1 : a > bound ? 1 : 0
  }
  return -1
}

/** 后端 `$item['date'] == $todayArr[0]`（松散比较；对日期串就是逐字相等）。 */
function sameDate(itemDate, today) {
  if (typeof itemDate === 'string') return itemDate === today
  if (itemDate === null || itemDate === undefined) return today === ''
  if (typeof itemDate === 'boolean') return itemDate === phpTruthy(today)
  if (typeof itemDate === 'number') return phpFloat(today) === itemDate
  return false
}

const listOf = (v) => {
  if (Array.isArray(v)) return v
  if (v && typeof v === 'object') return Object.values(v)
  return []
}

const cloneJson = (v) => (v === undefined ? v : JSON.parse(JSON.stringify(v)))

/**
 * @param {object|null} surcharges get-venue 的 `settings.surcharges`（原样 JSON）
 * @param {{ date: string, time: string, weekDay: string }} now 门店时区的「现在」：'YYYY-MM-DD'、'HH:mm:ss'、小写英文星期
 * @returns {{ rate: number, settings: object }}
 *   rate 是小数（0.015 = 1.5%）；settings 与后端落库的 surchargeSettings 同形：
 *   date 命中 → `{ type: 'date', settings: <那一条> }`；weekly → `{ type: 'weekly', settings: <整个 weekly>, weekDay }`；
 *   都没有 → `{ type: 'date', settings: [] }`。
 */
export function resolveCurrentSurcharge(surcharges, now) {
  const s = surcharges && typeof surcharges === 'object' ? surcharges : {}
  const { date, time, weekDay } = now || {}
  let rate = 0
  let settings = { type: 'date', settings: [] }

  if (phpTruthy(s.date)) {
    listOf(s.date).forEach(item => {
      if (!item || typeof item !== 'object') return
      if (!sameDate(item.date, date)) return
      if (phpCompareNow(time, item.fromHour) >= 0 && phpCompareNow(time, item.toHour) <= 0) {
        rate = phpFloat(item.rate) / 100
        settings = { type: 'date', settings: cloneJson(item) }
      }
    })
  }

  if (phpTruthy(s.weekly) && rate === 0) {
    const day = s.weekly && typeof s.weekly === 'object' && !Array.isArray(s.weekly) ? s.weekly[weekDay] : undefined
    if (day && typeof day === 'object' && phpTruthy(day.enable)) {
      rate = phpFloat(day.rate) / 100
      settings = { type: 'weekly', settings: cloneJson(s.weekly), weekDay }
    }
  }

  return { rate, settings }
}

/**
 * 刷卡手续费率，与后端 `Venue::getMerchantRate`（Venue.php 1459-1471）同义：
 * 开关按 PHP 真值（字符串 "false" 也算开，审计 Q6）；开着但费率为空取 0.0175；关着时 get-venue 也会下发 0.0175（Q7），必须先看开关。
 * @param {object|null} options get-venue 的 `settings.options`
 * @returns {number} 小数（0.0175 = 1.75%）
 */
export function resolveMerchantRate(options) {
  if (!options || typeof options !== 'object') return 0
  if (!phpTruthy(options.enableCustomerMerchantFee)) return 0
  if (!phpTruthy(options.customerMerchantRate)) return 0.0175
  return phpFloat(options.customerMerchantRate)
}
